import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/pro-regular-svg-icons';
import { CenteredModal } from '../../../UI/Modal/CenteredModal';
import { Input } from '../../../UI/V3/Forms/Input/Input';
import { Select } from '../../../UI/V3/Forms/Select/Select';
import Button from '../../../UI/V3/Button/Button';
import styles from './AddUserModal.module.scss';
import { saveUserThunk } from 'actions/user/UserActions';
import {
    saveOrganizationManagerThunk,
    updateOrganizationManagerThunk
} from 'actions/organizationManager/OrganizationManagerActions';
import { showSuccessThunk, showErrorThunk } from '@spike/notifications-action';
import { RootState } from 'store';
import { RootAction } from 'actions/ActionTypes';
import { OrganizationManagerUser } from 'model/organizationManager';
import { ORG_ROLE_OPTIONS } from 'model/user';

interface AddUserModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    editingUser?: OrganizationManagerUser | null;
}

interface UserFormData {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    confirmPassword: string;
    /** Role within the organization being managed. */
    accessLevelId: number;
}

export const AddUserModal: React.FC<AddUserModalProps> = ({ isOpen, onClose, onSuccess, editingUser }) => {
    const dispatch = useDispatch<ThunkDispatch<RootState, null, RootAction>>();
    const { organizations } = useSelector((state: RootState) => state.organizations);

    const [formData, setFormData] = useState<UserFormData>({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
        accessLevelId: 1
    });

    const [errors, setErrors] = useState<Partial<UserFormData>>({});
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Populate form when editing user
    useEffect(() => {
        if (editingUser) {
            setFormData({
                firstName: editingUser.firstName || '',
                lastName: editingUser.lastName || '',
                email: editingUser.email || '',
                password: '',
                confirmPassword: '',
                // Per-organization role, not the account-wide derived level
                accessLevelId: editingUser.accessLevelId || 1
            });
        } else {
            setFormData({
                firstName: '',
                lastName: '',
                email: '',
                password: '',
                confirmPassword: '',
                accessLevelId: 1
            });
        }
    }, [editingUser]);

    const handleInputChange = (field: keyof UserFormData, value: string) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: undefined
            }));
        }
    };

    const handleRoleChange = (value: string) => {
        setFormData(prev => ({
            ...prev,
            accessLevelId: parseInt(value, 10)
        }));
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<UserFormData> = {};

        if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
        if (formData.firstName.length > 50) newErrors.firstName = 'First name must not exceed 50 characters';
        if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
        if (formData.lastName.length > 50) newErrors.lastName = 'Last name must not exceed 50 characters';
        if (!formData.email.trim()) newErrors.email = 'Email is required';

        // Email validation
        if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        //Password validation
        if (!formData.password || !formData.password.trim()) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }

        if (!formData.confirmPassword || !formData.confirmPassword.trim()) {
            newErrors.confirmPassword = 'Please confirm your password';
        } else if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            return;
        }

        setIsSaving(true);

        try {
            const parentOrganization = organizations.find(org => !org.parentOrganizationId);
            const isEditing = Boolean(editingUser?.id);

            // Build user data object.
            // org_access_level_id seeds the account so it exists before a
            // membership can reference it; the backend re-derives it from the
            // person's memberships once the membership write lands.
            const userData = {
                ...(isEditing && { id: editingUser!.id }),
                orgAccessLevelId: formData.accessLevelId,
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                password: formData.password,
                passwordConfirmation: formData.confirmPassword,
                phone: '',
                emailOptout: false,
                admin: false,
                active: true,
                marketplace_id: editingUser?.marketplaceId
            };

            // Save user (create or update)

            const savedUser = await dispatch(saveUserThunk({ user: userData }));

            // The role lives on the membership, so every save needs a second
            // write: create the membership for a new user, update it for an
            // existing one. Without the update branch a role edit is a no-op.
            if (isEditing) {
                if (editingUser?.organizationManagerId) {
                    await dispatch(
                        updateOrganizationManagerThunk(editingUser.organizationManagerId, {
                            access_level_id: formData.accessLevelId,
                            // Round-tripped so a role change cannot strip ownership
                            owner: editingUser.owner
                        })
                    );
                }
            } else if (parentOrganization && savedUser?.id) {
                await dispatch(
                    saveOrganizationManagerThunk({
                        organization_id: parentOrganization.id,
                        user_id: savedUser.id,
                        access_level_id: formData.accessLevelId,
                        owner: false
                    })
                );
            }

            // Show success notification with appropriate message
            dispatch(showSuccessThunk(`User ${isEditing ? 'updated' : 'added'} successfully`) as any);

            onSuccess?.();
            handleClose();
        } catch (error: any) {
            // Error notification is already dispatched by the thunks
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        handleClose();
    };

    const handleClose = () => {
        // Reset form when closing
        setFormData({
            firstName: '',
            lastName: '',
            email: '',
            password: '',
            confirmPassword: '',
            accessLevelId: 1
        });
        setErrors({});
        setShowPassword(false);
        setShowConfirmPassword(false);
        onClose();
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const toggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    return (
        <CenteredModal
            isOpen={isOpen}
            onClose={handleClose}
            maxWidth="medium"
            className={styles.addUserModal}
        >
            <CenteredModal.Header
                title={editingUser ? 'Edit user' : 'Add user'}
                showCloseButton={true}
            />

            <CenteredModal.Content className={styles.modalContent}>
                <form
                    className={styles.form}
                    onSubmit={e => e.preventDefault()}
                >
                    {/* First Name and Last Name Row */}
                    <div className={styles.formRow}>
                        <div className={styles.formField}>
                            <Input
                                id="firstName"
                                label="First name"
                                value={formData.firstName}
                                onChange={e => handleInputChange('firstName', e.target.value)}
                                placeholder="Enter first name"
                                error={errors.firstName}
                                maxLength={50}
                            />
                        </div>
                        <div className={styles.formField}>
                            <Input
                                id="lastName"
                                label="Last name"
                                value={formData.lastName}
                                onChange={e => handleInputChange('lastName', e.target.value)}
                                placeholder="Enter last name"
                                error={errors.lastName}
                                maxLength={50}
                            />
                        </div>
                    </div>

                    {/* Role */}
                    <div className={styles.formField}>
                        <Select
                            label="Role"
                            options={ORG_ROLE_OPTIONS}
                            value={formData.accessLevelId.toString()}
                            onChange={handleRoleChange}
                            placeholder="Select a role"
                        />
                    </div>

                    {/* Email */}
                    <div className={styles.formField}>
                        <Input
                            id="email"
                            label="Email"
                            type="email"
                            value={formData.email}
                            onChange={e => handleInputChange('email', e.target.value)}
                            placeholder="Enter email address"
                            error={errors.email}
                        />
                    </div>

                    {/* Password */}
                    <div className={styles.formField}>
                        <div className={styles.passwordField}>
                            <Input
                                id="password"
                                label="Password"
                                type={showPassword ? 'text' : 'password'}
                                value={formData.password}
                                onChange={e => handleInputChange('password', e.target.value)}
                                placeholder="Enter the password"
                                error={errors.password}
                            />
                            <button
                                type="button"
                                className={styles.passwordToggle}
                                onClick={togglePasswordVisibility}
                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                            >
                                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                            </button>
                        </div>
                    </div>

                    {/* Confirm Password */}
                    <div className={styles.formField}>
                        <div className={styles.passwordField}>
                            <Input
                                id="confirmPassword"
                                label="Confirm password"
                                type={showConfirmPassword ? 'text' : 'password'}
                                value={formData.confirmPassword}
                                onChange={e => handleInputChange('confirmPassword', e.target.value)}
                                placeholder="Confirm the password"
                                error={errors.confirmPassword}
                            />
                            <button
                                type="button"
                                className={styles.passwordToggle}
                                onClick={toggleConfirmPasswordVisibility}
                                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                            >
                                <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                            </button>
                        </div>
                    </div>

                    {/* Form Actions */}
                    <div className={styles.formActions}>
                        <Button
                            label="Cancel"
                            variant="outlined"
                            size="large"
                            onClick={handleCancel}
                            disabled={isSaving}
                        />
                        <Button
                            label={isSaving ? 'Saving...' : 'Save'}
                            variant="black"
                            size="large"
                            onClick={handleSave}
                            disabled={isSaving}
                        />
                    </div>
                </form>
            </CenteredModal.Content>
        </CenteredModal>
    );
};

export default AddUserModal;
